// X159 (Grant 2026-10-27 on Club settings › Logo: "you can't enlarge, pan and zoom the current club logo here"; he
// chose: just view it larger). The maths behind TlImageViewer, pure so it is unit-tested without a browser.
//
// Model: the image is laid out at its FIT size (the largest size that fits the stage, centred), then the stage applies
// `translate(pan) scale(zoom)` about the stage centre. zoom 1 = fit, ZOOM_MAX = 5. pan is in screen pixels, measured
// from the stage centre, and is clamped so the image can never be thrown off screen: when the zoomed image is smaller
// than the stage on an axis it stays centred (pan 0); when larger, its edges never come inside the stage edges.

export const ZOOM_MIN = 1;
export const ZOOM_MAX = 5;
/** One press of + or −, one keyboard step. */
export const ZOOM_STEP = 1.5;
/** Double-click / double-tap toggles fit ↔ this, about the point. */
export const ZOOM_TOGGLE = 2;
/** Two pointer-ups this close in time and space are a double-tap. */
export const DOUBLE_TAP_MS = 320;
export const DOUBLE_TAP_PX = 30;

export function clampZoom(zoom, min = ZOOM_MIN, max = ZOOM_MAX) {
  if (!Number.isFinite(zoom)) return min;
  return Math.min(max, Math.max(min, zoom));
}

/** The image's fit size inside the stage (never upscaled past the stage; small images are shown at fit too). */
export function fitSize(imageWidth, imageHeight, stageWidth, stageHeight) {
  if (!(imageWidth > 0) || !(imageHeight > 0) || !(stageWidth > 0) || !(stageHeight > 0)) return {width: 0, height: 0, scale: 0};
  const scale = Math.min(stageWidth / imageWidth, stageHeight / imageHeight);
  return {width: imageWidth * scale, height: imageHeight * scale, scale};
}

/** Pan bounds for a zoom: how far the centre may move on each axis (0 when the image fits on that axis). */
export function panBounds(fit, zoom, stageWidth, stageHeight) {
  return {
    x: Math.max(0, (fit.width * zoom - stageWidth) / 2),
    y: Math.max(0, (fit.height * zoom - stageHeight) / 2),
  };
}

export function clampPan(pan, fit, zoom, stageWidth, stageHeight) {
  const b = panBounds(fit, zoom, stageWidth, stageHeight);
  const x = Number.isFinite(pan?.x) ? pan.x : 0;
  const y = Number.isFinite(pan?.y) ? pan.y : 0;
  // `|| 0` turns a −0 (Math.max(−0, …)) into 0 so equality checks and CSS read plainly.
  return {x: Math.min(b.x, Math.max(-b.x, x)) || 0, y: Math.min(b.y, Math.max(-b.y, y)) || 0};
}

/**
 * Zoom to `nextZoom` keeping the image point under `point` (screen px from the stage centre) where it is, then
 * clamp. The point under the cursor: p = pan + zoom · q, with q the fit-space offset; keeping p fixed under the new
 * zoom gives pan' = p − (zoom'/zoom) · (p − pan).
 */
export function zoomAt(state, nextZoom, point, fit, stageWidth, stageHeight) {
  const zoom = clampZoom(nextZoom);
  const from = state.zoom > 0 ? state.zoom : 1;
  const px = Number.isFinite(point?.x) ? point.x : 0;
  const py = Number.isFinite(point?.y) ? point.y : 0;
  const ratio = zoom / from;
  const pan = {x: px - ratio * (px - state.pan.x), y: py - ratio * (py - state.pan.y)};
  return {zoom, pan: clampPan(pan, fit, zoom, stageWidth, stageHeight)};
}

/** Wheel: a notch zooms by a little; a trackpad pinch (ctrlKey) sends small deltas, so it is the same curve. */
export function wheelZoom(zoom, deltaY) {
  const d = Number.isFinite(deltaY) ? deltaY : 0;
  return clampZoom(zoom * Math.exp(-d * 0.0022));
}

/** A two-finger pinch: the zoom at the pinch start scaled by how far the fingers have spread. */
export function pinchZoom(startZoom, startDistance, distance) {
  if (!(startDistance > 0) || !(distance > 0)) return clampZoom(startZoom);
  return clampZoom(startZoom * (distance / startDistance));
}

/** Double-click / double-tap: fit when zoomed in, otherwise ZOOM_TOGGLE. */
export function toggleZoom(zoom) {
  return zoom > ZOOM_MIN + 0.01 ? ZOOM_MIN : ZOOM_TOGGLE;
}

/** Drag by (dx, dy) screen px, bounded. */
export function panBy(state, dx, dy, fit, stageWidth, stageHeight) {
  const pan = {x: state.pan.x + (Number.isFinite(dx) ? dx : 0), y: state.pan.y + (Number.isFinite(dy) ? dy : 0)};
  return {zoom: state.zoom, pan: clampPan(pan, fit, state.zoom, stageWidth, stageHeight)};
}

/** The keyboard: + and = zoom in, − and _ zoom out, 0 fits; anything else is not ours (null). */
export function keyZoom(key, zoom) {
  switch (key) {
    case '+': case '=': case 'Add': return clampZoom(zoom * ZOOM_STEP);
    case '-': case '_': case 'Subtract': return clampZoom(zoom / ZOOM_STEP);
    case '0': return ZOOM_MIN;
    default: return null;
  }
}

/** Two pointer-ups make a double-tap when they are close in time and place. */
export function isDoubleTap(previous, current) {
  if (!previous || !current) return false;
  return current.time - previous.time <= DOUBLE_TAP_MS
    && Math.hypot(current.x - previous.x, current.y - previous.y) <= DOUBLE_TAP_PX;
}

/** The readout: "Fit" at 1, else "2×" / "2.5×". */
export function zoomLabel(zoom) {
  if (zoom <= ZOOM_MIN + 0.001) return 'Fit';
  const rounded = Math.round(zoom * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}×`;
}

export function initialState() {
  return {zoom: ZOOM_MIN, pan: {x: 0, y: 0}};
}
